// SuperCompanyCreatePage — /super/companies/new.
//
// Provisions a new tenant in a single transaction via
// POST /api/v1/super/companies. Two side-by-side sections plus a third
// Modules block:
//   1. Company details (name / subdomain / email / phone)
//   2. Primary admin account (name / email / username / password +
//      "Send welcome email" toggle)
//   3. Modules — at least one of collections / skip / weighbridge must
//      be selected. Each row carries its own tier + (where applicable)
//      a mobile-dispatch toggle. The top-level `companies.tier` column
//      (still required by the server schema) is derived from whichever
//      selected module tier ranks highest.
//
// Server enforces uniqueness for both `subdomain` and
// `primary_admin.username` and returns 400 with field-level error map.
// We surface those errors next to the relevant input.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, ShieldCheck } from 'lucide-react';
import type { AxiosError } from 'axios';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Select } from '../../components/form/Select';
import { Checkbox } from '../../components/form/Checkbox';
import {
  MODULE_KEYS,
  MODULE_TIER_KEYS,
  useCreateCompany,
  type CreateCompanyInput,
  type ModuleKey,
  type ModuleTierKey,
} from './hooks/useSuper';
import styles from './SuperCompanyCreatePage.module.css';

const MODULE_TIER_OPTIONS = MODULE_TIER_KEYS.map((t) => ({
  value: t,
  label: t.charAt(0).toUpperCase() + t.slice(1),
}));

const MODULE_LABELS: Record<ModuleKey, string> = {
  collections: 'Collections',
  skip: 'Skip',
  weighbridge: 'Weighbridge',
};

interface ModuleSelectionRow {
  enabled: boolean;
  tier: ModuleTierKey;
  mobile: boolean;
}

type ModuleSelections = Record<ModuleKey, ModuleSelectionRow>;

const EMPTY_MODULES: ModuleSelections = {
  collections: { enabled: false, tier: 'starter', mobile: true },
  skip: { enabled: false, tier: 'starter', mobile: true },
  weighbridge: { enabled: false, tier: 'starter', mobile: false },
};

interface FormState {
  name: string;
  subdomain: string;
  email: string;
  phone: string;
  admin_name: string;
  admin_email: string;
  admin_username: string;
  admin_password: string;
  send_welcome_email: boolean;
  modules: ModuleSelections;
}

const EMPTY: FormState = {
  name: '',
  subdomain: '',
  email: '',
  phone: '',
  admin_name: '',
  admin_email: '',
  admin_username: '',
  admin_password: '',
  send_welcome_email: true,
  modules: EMPTY_MODULES,
};

interface ServerErrorBody {
  message?: string;
  fields?: Record<string, string>;
}

function extractServerError(err: unknown): ServerErrorBody | null {
  const axiosErr = err as AxiosError<ServerErrorBody>;
  if (axiosErr?.response?.data && typeof axiosErr.response.data === 'object') {
    return axiosErr.response.data;
  }
  return null;
}

const TIER_RANK: Record<ModuleTierKey, number> = {
  starter: 0,
  professional: 1,
  enterprise: 2,
};

/**
 * The server still requires a top-level `tier` on the company row (the
 * legacy companies.tier column). Pick whichever selected-module tier
 * ranks highest so the company-level field broadly reflects the most
 * generous plan the tenant is paying for.
 */
function deriveTopLevelTier(modules: ModuleSelections): ModuleTierKey {
  let best: ModuleTierKey = 'starter';
  for (const key of MODULE_KEYS) {
    const row = modules[key];
    if (!row.enabled) continue;
    if (TIER_RANK[row.tier] > TIER_RANK[best]) {
      best = row.tier;
    }
  }
  return best;
}

function moduleSupportsMobile(m: ModuleKey): boolean {
  return m !== 'weighbridge';
}

export function SuperCompanyCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [modulesError, setModulesError] = useState<string | null>(null);
  const create = useCreateCompany();

  const serverError = extractServerError(create.error);
  const fieldErrors = serverError?.fields ?? {};
  const generalError =
    create.isError && Object.keys(fieldErrors).length === 0
      ? serverError?.message ?? 'Failed to create company.'
      : null;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function setModuleField(
    module: ModuleKey,
    patch: Partial<ModuleSelectionRow>,
  ) {
    setForm((f) => ({
      ...f,
      modules: {
        ...f.modules,
        [module]: { ...f.modules[module], ...patch },
      },
    }));
    setModulesError(null);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const selected = MODULE_KEYS.filter((m) => form.modules[m].enabled);
    if (selected.length === 0) {
      setModulesError('Select at least one module.');
      return;
    }
    setModulesError(null);

    const modules: CreateCompanyInput['modules'] = selected.map((m) => {
      const row = form.modules[m];
      const entry: NonNullable<CreateCompanyInput['modules']>[number] = {
        module: m,
        tier: row.tier,
      };
      if (moduleSupportsMobile(m)) {
        entry.mobile_dispatch_enabled = row.mobile;
      }
      return entry;
    });

    const payload: CreateCompanyInput = {
      name: form.name.trim(),
      subdomain: form.subdomain.trim().toLowerCase(),
      email: form.email.trim(),
      phone: form.phone.trim() === '' ? null : form.phone.trim(),
      tier: deriveTopLevelTier(form.modules),
      primary_admin: {
        name: form.admin_name.trim(),
        email: form.admin_email.trim(),
        username: form.admin_username.trim(),
        password: form.admin_password,
      },
      send_welcome_email: form.send_welcome_email,
      modules,
    };
    create.mutate(payload);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Company"
        subtitle="Provision a new tenant account"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/super/companies')}
          >
            Cancel
          </Button>
        }
      />

      {generalError ? (
        <Card>
          <p className={styles.generalError} role="alert">
            {generalError}
          </p>
        </Card>
      ) : null}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.grid}>
          <Card title="Company details">
            <div className={styles.fields}>
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                required
                error={
                  fieldErrors.name !== undefined
                    ? { message: fieldErrors.name }
                    : undefined
                }
              />
              <Input
                label="Subdomain"
                value={form.subdomain}
                onChange={(e) => setField('subdomain', e.target.value.toLowerCase())}
                required
                helper="e.g. acme-recycling — used for internal reference"
                error={
                  fieldErrors.subdomain !== undefined
                    ? { message: fieldErrors.subdomain }
                    : undefined
                }
              />
              <Input
                label="Contact Email"
                variant="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                required
                error={
                  fieldErrors.email !== undefined
                    ? { message: fieldErrors.email }
                    : undefined
                }
              />
              <Input
                label="Contact Phone"
                variant="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                helper="Optional"
              />
            </div>
          </Card>

          <Card title="Primary Admin Account">
            <div className={styles.fields}>
              <Input
                label="Full Name"
                value={form.admin_name}
                onChange={(e) => setField('admin_name', e.target.value)}
                required
                error={
                  fieldErrors['primary_admin.name'] !== undefined
                    ? { message: fieldErrors['primary_admin.name'] }
                    : undefined
                }
              />
              <Input
                label="Email Address"
                variant="email"
                value={form.admin_email}
                onChange={(e) => setField('admin_email', e.target.value)}
                required
                error={
                  fieldErrors['primary_admin.email'] !== undefined
                    ? { message: fieldErrors['primary_admin.email'] }
                    : undefined
                }
              />
              <Input
                label="Username"
                value={form.admin_username}
                onChange={(e) => setField('admin_username', e.target.value)}
                required
                error={
                  fieldErrors['primary_admin.username'] !== undefined
                    ? { message: fieldErrors['primary_admin.username'] }
                    : undefined
                }
              />
              <Input
                label="Password"
                variant="password"
                value={form.admin_password}
                onChange={(e) => setField('admin_password', e.target.value)}
                required
                helper="Minimum 8 characters."
                error={
                  fieldErrors['primary_admin.password'] !== undefined
                    ? { message: fieldErrors['primary_admin.password'] }
                    : undefined
                }
              />
              <div>
                <Checkbox
                  label="Send welcome email"
                  checked={form.send_welcome_email}
                  onChange={(e) => setField('send_welcome_email', e.target.checked)}
                />
                <p className={styles.checkboxHelper}>
                  Sends a password reset link so the admin can set their own
                  password on first login.
                </p>
              </div>
            </div>
          </Card>
        </div>

        <Card title="Modules">
          <p className={styles.modulesHint}>
            Select at least one module. Each module gets its own tier and
            (where applicable) a mobile-dispatch toggle.
          </p>
          <div className={styles.moduleList}>
            {MODULE_KEYS.map((m) => {
              const row = form.modules[m];
              const supportsMobile = moduleSupportsMobile(m);
              return (
                <div key={m} className={styles.moduleRow} data-module={m}>
                  <div className={styles.moduleCheck}>
                    <Checkbox
                      label={MODULE_LABELS[m]}
                      checked={row.enabled}
                      onChange={(e) =>
                        setModuleField(m, { enabled: e.target.checked })
                      }
                    />
                  </div>
                  <Select
                    label="Tier"
                    value={row.tier}
                    onChange={(e) =>
                      setModuleField(m, {
                        tier: e.target.value as ModuleTierKey,
                      })
                    }
                    options={MODULE_TIER_OPTIONS}
                    disabled={!row.enabled}
                  />
                  {supportsMobile ? (
                    <Checkbox
                      label="Mobile dispatch"
                      checked={row.mobile}
                      onChange={(e) =>
                        setModuleField(m, { mobile: e.target.checked })
                      }
                      disabled={!row.enabled}
                    />
                  ) : (
                    <span className={styles.moduleSpacer} aria-hidden />
                  )}
                </div>
              );
            })}
          </div>
          {modulesError !== null ? (
            <p className={styles.modulesError} role="alert">
              {modulesError}
            </p>
          ) : null}
        </Card>

        <Card>
          <div className={styles.footer}>
            <p className={styles.footerHint}>
              <ShieldCheck size={14} aria-hidden /> Provisioning runs as a
              single transaction. If anything fails the entire creation rolls
              back — no partial company is ever persisted.
            </p>
            <Button
              type="submit"
              variant="primary"
              icon={Building2}
              loading={create.isPending}
            >
              {create.isPending ? 'Creating...' : 'Create Company'}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
